import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Grid } from '../../design-system'
import { Clock, Trophy, Brain, ArrowRight, Star, Zap, Users, TrendingUp, Award, CheckCircle, X } from 'lucide-react'
import { useResponsive } from '../../hooks/useResponsive'
import { useTest } from '../../contexts/TestContext'

/**
 * Test Mode Selection Component
 * Adaptive design with mobile-first approach and desktop enhancements
 * Includes upgrade prompts, feature comparisons, and responsive behavior
 */

const TestModeSelector = ({ onSelectMode, className, userProfile = null, isLoading = false }) => {
  const [selectedMode, setSelectedMode] = useState(null)
  const [showComparison, setShowComparison] = useState(false)
  const [isComprehensiveExpanded, setIsComprehensiveExpanded] = useState(false)
  const { isMobile, getAdaptiveColumns } = useResponsive()
  const { selectMode, startTest } = useTest()
  const navigate = useNavigate()
  // Enhanced test modes with more detailed features and upgrade prompts
  const testModes = [
    {
      id: 'free',
      title: 'Free Test',
      subtitle: '10 Questions',
      description: 'Quick cognitive assessment with instant results',
      duration: '5-8 minutes',
      price: 'Free',
      features: [
        'Basic IQ scoring',
        'General cognitive assessment',
        'Instant results',
        'No registration required',
        'Social sharing',
        'Quick 5-8 minute test'
      ],
      limitations: [
        'Limited question variety',
        'Basic analysis only',
        'No detailed breakdown',
        'No progress tracking'
      ],
      icon: <Zap className="w-6 h-6" />,
      color: 'primary',
      popular: false,
      recommended: false
    },
    {
      id: 'comprehensive',
      title: 'Comprehensive Test',
      subtitle: '25 Questions',
      description: 'Detailed cognitive analysis with category breakdown',
      duration: '8-15 minutes',
      price: 'Premium',
      features: [
        'Detailed IQ analysis',
        'Category-wise performance breakdown',
        'Percentile ranking comparison',
        'Performance insights & recommendations',
        'Test history tracking (requires account)',
        'Progress monitoring over time',
        'Advanced social features',
        'Detailed explanations for answers',
        'Cognitive strengths analysis',
        'No duplicate questions from free test'
      ],
      advantages: [
        '2.5x more questions for accuracy',
        'Professional-grade analysis',
        'Track improvement over time',
        'Compare with global users'
      ],
      icon: <Brain className="w-6 h-6" />,
      color: 'secondary',
      popular: true,
      recommended: true
    }
  ]

  // Handle mode selection with confirmation
  const handleModeSelect = async (modeId) => {
    setSelectedMode(modeId)
    
    // Show comparison for free users selecting comprehensive
    if (modeId === 'comprehensive' && !userProfile?.isPremium) {
      setShowComparison(true)
      return
    }
    
    try {
      // Update test context and start test
      selectMode(modeId)

      // Call parent callback if provided
      if (onSelectMode) {
        onSelectMode(modeId)
      }

      // Start the test automatically
      await startTest(modeId)
    } catch (error) {
      console.error('Failed to start test:', error)
      // Reset selection on error
      setSelectedMode(null)
    }
  }

  // Handle upgrade confirmation
  const handleUpgradeConfirm = async () => {
    setShowComparison(false)
    
    try {
      selectMode('comprehensive')
      
      if (onSelectMode) {
        onSelectMode('comprehensive')
      }
      
      await startTest('comprehensive')
    } catch (error) {
      console.error('Failed to start comprehensive test:', error)
      setSelectedMode(null)
    }
  }

  // Handle downgrade to free
  const handleSelectFree = async () => {
    setShowComparison(false)
    
    try {
      selectMode('free')
      
      if (onSelectMode) {
        onSelectMode('free')
      }
      
      await startTest('free')
    } catch (error) {
      console.error('Failed to start free test:', error)
      setSelectedMode(null)
    }
  }

  // Get user's test history stats
  const getUserStats = () => {
    if (!userProfile) return null
    
    return {
      testsCompleted: userProfile.statistics?.testsCompleted || 0,
      averageIQ: userProfile.statistics?.averageIQ || 0,
      bestIQ: userProfile.statistics?.bestIQ || 0,
      lastTestDate: userProfile.statistics?.lastTestDate
    }
  }

  const userStats = getUserStats()

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showComparison) {
        setShowComparison(false)
      }
    }

    if (showComparison) {
      document.addEventListener('keydown', handleKeyDown)
      // Focus trap - prevent scrolling background
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [showComparison])

  return (
    <div className={className}>
      {/* Header Section */}
      <div className="text-center mb-8 lg:mb-12">
        <h1 className="text-adaptive-4xl font-bold text-text-primary mb-4">
          Choose Your IQ Test
        </h1>
        <p className="text-adaptive-lg text-text-secondary max-w-2xl mx-auto">
          Select the test format that best suits your needs. Both tests use scientifically validated questions.
        </p>
        
        {/* User Stats Display */}
        {userStats && userStats.testsCompleted > 0 && (
          <div className="mt-6 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="text-center">
                <div className="font-semibold text-primary-600">{userStats.testsCompleted}</div>
                <div className="text-text-tertiary">Tests Taken</div>
              </div>
              {userStats.bestIQ > 0 && (
                <div className="text-center">
                  <div className="font-semibold text-secondary-600">{userStats.bestIQ}</div>
                  <div className="text-text-tertiary">Best Score</div>
                </div>
              )}
              <div className="text-center">
                <TrendingUp className="w-4 h-4 mx-auto text-success-500 mb-1" />
                <div className="text-text-tertiary">Improving</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test Mode Cards */}
      <div className="flex flex-col md:flex-row justify-center items-stretch gap-6 max-w-5xl mx-auto px-4">
        {testModes.map((mode) => (
          <Card
            key={mode.id}
            adaptive
            hover
            className={`relative transition-all duration-200 w-full md:max-w-md ${
              selectedMode === mode.id ? 'ring-2 ring-primary-500 shadow-lg' : ''
            }`}
          >

            <CardHeader>
              {/* Icon, Title and Price */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    mode.color === 'primary' ? 'bg-primary-100 text-primary-600' : 'bg-secondary-100 text-secondary-600'
                  }`}>
                    {mode.icon}
                  </div>
                  <div>
                    <CardTitle adaptive className="mb-1">
                      {mode.title}
                    </CardTitle>
                    <CardDescription adaptive className="text-lg font-medium">
                      {mode.subtitle}
                    </CardDescription>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  mode.price === 'Free' 
                    ? 'bg-success-100 text-success-700' 
                    : 'bg-warning-100 text-warning-700'
                }`}>
                  {mode.price}
                </div>
              </div>

              {/* Description */}
              <p className="text-adaptive-base text-text-secondary mb-4">
                {mode.description}
              </p>

              {/* Duration and Stats */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-text-tertiary" />
                  <span className="text-adaptive-sm text-text-secondary">
                    {mode.duration}
                  </span>
                </div>
                {mode.id === 'comprehensive' && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-text-tertiary" />
                    <span className="text-adaptive-sm text-text-secondary">
                      85% accuracy
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {/* Features List */}
              <div className="space-y-3 min-h-[240px] flex flex-col">
                {(mode.id === 'comprehensive' && !isComprehensiveExpanded
                  ? mode.features.slice(0, 5)
                  : mode.features
                ).map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${
                      mode.color === 'primary' ? 'text-primary-500' : 'text-secondary-500'
                    }`} />
                    <span className="text-adaptive-sm text-text-secondary">
                      {feature}
                    </span>
                  </div>
                ))}
                {mode.id === 'comprehensive' && !isComprehensiveExpanded && (
                  <div className="flex justify-end mt-auto">
                    <button
                      onClick={() => setIsComprehensiveExpanded(true)}
                      className="text-lg text-secondary-600 hover:text-secondary-700 font-semibold"
                    >
                      more →
                    </button>
                  </div>
                )}
              </div>

              {/* Advantages for comprehensive test */}
              {mode.advantages && isComprehensiveExpanded && (
                <div className="mb-6 p-3 bg-secondary-50 rounded-lg">
                  <h4 className="text-sm font-medium text-secondary-700 mb-2">Why Choose Comprehensive?</h4>
                  <div className="space-y-1">
                    {mode.advantages.map((advantage, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <TrendingUp className="w-3 h-3 text-secondary-500" />
                        <span className="text-xs text-secondary-600">{advantage}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Button */}
              <Button
                variant={mode.color === 'primary' ? 'default' : 'secondary'}
                adaptive
                className="w-full"
                rightIcon={<ArrowRight className="w-4 h-4" />}
                onClick={() => handleModeSelect(mode.id)}
                disabled={isLoading || selectedMode === mode.id}
                loading={selectedMode === mode.id && isLoading}
              >
                {selectedMode === mode.id && isLoading 
                  ? 'Starting Test...' 
                  : `Start ${mode.title}`
                }
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upgrade Comparison Modal */}
      {showComparison && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => e.target === e.currentTarget && setShowComparison(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
        >
          <div className={`bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto ${
            isMobile ? 'max-w-full mx-2' : 'max-w-2xl'
          }`}>
            <div className="p-4 lg:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 id="upgrade-modal-title" className="text-xl lg:text-2xl font-bold text-text-primary">
                  Upgrade to Comprehensive Test?
                </h3>
                <button
                  onClick={() => setShowComparison(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-text-secondary mb-4">
                  Get 2.5x more accurate results with detailed analysis and insights.
                </p>
                <div className="bg-gradient-to-r from-secondary-50 to-primary-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-secondary-600" />
                    <span className="font-medium text-secondary-700">Why upgrade?</span>
                  </div>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• 25 questions vs 10 for higher accuracy</li>
                    <li>• Detailed category breakdown (numerical, logical, verbal, spatial)</li>
                    <li>• Percentile ranking among all users</li>
                    <li>• No duplicate questions from your free test</li>
                  </ul>
                </div>
              </div>
              
              {/* Comparison Table */}
              <div className={`mb-6 ${isMobile ? 'space-y-4' : 'grid grid-cols-3 gap-4'}`}>
                {!isMobile && (
                  <>
                    <div className="text-sm font-medium text-text-tertiary">Feature</div>
                    <div className="text-sm font-medium text-center">Free Test</div>
                    <div className="text-sm font-medium text-center">Comprehensive</div>
                  </>
                )}
                
                {/* Mobile-friendly comparison */}
                {isMobile ? (
                  <div className="space-y-4">
                    {[
                      { feature: 'Questions', free: '10', comprehensive: '25', highlight: true },
                      { feature: 'Duration', free: '5-8 min', comprehensive: '8-15 min' },
                      { feature: 'Category Analysis', free: 'Basic', comprehensive: 'Detailed', highlight: true },
                      { feature: 'Progress Tracking', free: '❌', comprehensive: '✅' },
                      { feature: 'Percentile Ranking', free: '❌', comprehensive: '✅', highlight: true },
                      { feature: 'Question Duplicates', free: 'Possible', comprehensive: 'None from free test', highlight: true }
                    ].map((item, index) => (
                      <div key={index} className={`p-3 rounded-lg ${
                        item.highlight ? 'bg-secondary-50 border border-secondary-200' : 'bg-gray-50'
                      }`}>
                        <div className="font-medium text-sm text-text-primary mb-2">{item.feature}</div>
                        <div className="flex justify-between">
                          <div className="text-sm text-gray-600">Free: {item.free}</div>
                          <div className="text-sm font-medium text-secondary-600">Comprehensive: {item.comprehensive}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-text-secondary">Questions</div>
                    <div className="text-sm text-center">10</div>
                    <div className="text-sm text-center font-medium text-secondary-600">25</div>
                    
                    <div className="text-sm text-text-secondary">Duration</div>
                    <div className="text-sm text-center">5-8 min</div>
                    <div className="text-sm text-center font-medium text-secondary-600">8-15 min</div>
                    
                    <div className="text-sm text-text-secondary">Category Analysis</div>
                    <div className="text-sm text-center">Basic</div>
                    <div className="text-sm text-center font-medium text-secondary-600">Detailed</div>
                    
                    <div className="text-sm text-text-secondary">Progress Tracking</div>
                    <div className="text-sm text-center">❌</div>
                    <div className="text-sm text-center font-medium text-secondary-600">✅</div>
                    
                    <div className="text-sm text-text-secondary">Percentile Ranking</div>
                    <div className="text-sm text-center">❌</div>
                    <div className="text-sm text-center font-medium text-secondary-600">✅</div>
                    
                    <div className="text-sm text-text-secondary">Question Duplicates</div>
                    <div className="text-sm text-center">Possible</div>
                    <div className="text-sm text-center font-medium text-secondary-600">None from free</div>
                  </>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className={`flex gap-3 ${isMobile ? 'flex-col' : 'flex-row'}`}>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleSelectFree}
                  disabled={isLoading}
                >
                  Continue with Free
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={handleUpgradeConfirm}
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {isLoading ? 'Starting...' : 'Upgrade to Comprehensive'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="mt-12 lg:mt-16 space-y-8">
        {/* About Section */}
        <div className="text-center">
          <div className="bg-neutral-50 rounded-lg p-6 lg:p-8 max-w-4xl mx-auto">
            <h3 className="text-adaptive-lg font-semibold text-text-primary mb-6">
              About Our IQ Tests
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-adaptive-sm text-text-secondary">
              <div className="flex flex-col items-center gap-3">
                <Trophy className="w-10 h-10 text-warning-500" />
                <span className="font-medium text-text-primary">Scientifically Validated</span>
                <span className="text-center">Based on established cognitive assessment methods used by professionals</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <Brain className="w-10 h-10 text-success-500" />
                <span className="font-medium text-text-primary">Multiple Categories</span>
                <span className="text-center">Tests numerical, logical, verbal, and spatial reasoning abilities</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <Clock className="w-10 h-10 text-primary-500" />
                <span className="font-medium text-text-primary">Instant Results</span>
                <span className="text-center">Get your IQ score, analysis, and recommendations immediately</span>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-6 lg:p-8 max-w-4xl mx-auto">
            <h3 className="text-adaptive-lg font-semibold text-text-primary mb-6">
              Join Thousands of Test Takers
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-primary-600 mb-1">50K+</div>
                <div className="text-sm text-text-secondary">Tests Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-secondary-600 mb-1">4.8★</div>
                <div className="text-sm text-text-secondary">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-success-600 mb-1">95%</div>
                <div className="text-sm text-text-secondary">Accuracy Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-warning-600 mb-1">24/7</div>
                <div className="text-sm text-text-secondary">Available</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TestModeSelector